import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Plus, Search, Pencil, Trash2, Download, FileText, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const ListDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUrlImportDialog, setShowUrlImportDialog] = useState(false);
  const [editingGift, setEditingGift] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [giftToDelete, setGiftToDelete] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  
  // Filter and Sort states
  const [filterRecipient, setFilterRecipient] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterPriceRange, setFilterPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [newGift, setNewGift] = useState({
    gift_name: '',
    recipient_name: '',
    recipient_id: '',
    price: 0,
    description: '',
    store_name: '',
    store_url: '',
    image_url: '',
    priority: 'must_have',
    notes: '',
    is_purchased: false
  });

  useEffect(() => {
    fetchList();
    fetchGifts();
    fetchRecipients();
  }, [id]);

  const fetchRecipients = async () => {
    try {
      const response = await api.get('/recipients');
      setRecipients(response.data);
    } catch (error) {
      // User might not have premium, ignore error
      setRecipients([]);
    }
  };

  const fetchList = async () => {
    try {
      const response = await api.get(`/lists/${id}`);
      setList(response.data);
    } catch (error) {
      toast.error('Failed to load list');
    }
  };

  const fetchGifts = async () => {
    try {
      const response = await api.get(`/gifts?list_id=${id}`);
      setGifts(response.data);
    } catch (error) {
      toast.error('Failed to load gifts');
    } finally {
      setLoading(false);
    }
  };

  const addGift = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/gifts?list_id=${id}`, newGift);
      toast.success('Gift added!');
      setShowAddDialog(false);
      setNewGift({
        gift_name: '',
        recipient_name: '',
        recipient_id: '',
        price: 0,
        description: '',
        store_name: '',
        store_url: '',
        priority: 'must_have',
        notes: '',
        is_purchased: false
      });
      fetchGifts();
      fetchList();
    } catch (error) {
      toast.error('Failed to add gift');
    }
  };

  const togglePurchased = async (giftId) => {
    try {
      await api.patch(`/gifts/${giftId}/purchased`);
      fetchGifts();
      fetchList();
    } catch (error) {
      toast.error('Failed to update gift');
    }
  };

  const toggleReceived = async (giftId) => {
    if (user?.subscription_tier !== 'elite') {
      toast.error('Elite subscription required');
      navigate('/subscription');
      return;
    }
    
    try {
      await api.patch(`/gifts/${giftId}/received`);
      fetchGifts();
      fetchList();
    } catch (error) {
      toast.error('Failed to update gift');
    }
  };

  const deleteGift = async () => {
    if (!giftToDelete) return;
    
    try {
      await api.delete(`/gifts/${giftToDelete.id}`);
      toast.success('Gift deleted!');
      setGiftToDelete(null);
      fetchGifts();
      fetchList();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete gift');
    }
  };

  const handleDeleteClick = (gift, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setGiftToDelete(gift);
  };

  const exportCSV = async () => {
    if (user?.subscription_tier === 'free') {
      toast.error('Premium subscription required for exports');
      navigate('/subscription');
      return;
    }
    
    try {
      setExporting(true);
      const response = await api.get(`/exports/csv/${id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${list.name}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully!');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const exportPDF = async () => {
    if (user?.subscription_tier === 'free') {
      toast.error('Premium subscription required for exports');
      navigate('/subscription');
      return;
    }
    
    try {
      setExporting(true);
      const response = await api.get(`/exports/pdf/${id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${list.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully!');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const importFromUrl = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    try {
      setExtracting(true);
      const response = await api.post(`/gifts/extract-from-url?url=${encodeURIComponent(importUrl)}`);
      const extractedData = response.data;
      
      // Pre-fill the gift form with extracted data
      setNewGift({
        gift_name: extractedData.gift_name || '',
        recipient_name: '',
        recipient_id: '',
        price: extractedData.price || 0,
        description: '',
        store_name: extractedData.store_name || '',
        store_url: extractedData.store_url || importUrl,
        image_url: extractedData.image_url || '',
        priority: 'must_have',
        notes: '',
        is_purchased: false
      });
      
      // Close URL dialog and open add gift dialog
      setShowUrlImportDialog(false);
      setImportUrl('');
      setShowAddDialog(true);
      toast.success('Gift details extracted! Review and save.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract gift details');
    } finally {
      setExtracting(false);
    }
  };

  const openEditDialog = (gift) => {
    setEditingGift(gift);
    setNewGift({
      gift_name: gift.gift_name,
      recipient_name: gift.recipient_name,
      recipient_id: gift.recipient_id || '',
      price: gift.price,
      description: gift.description || '',
      store_name: gift.store_name || '',
      store_url: gift.store_url || '',
      priority: gift.priority,
      notes: gift.notes || '',
      is_purchased: gift.is_purchased
    });
    setShowEditDialog(true);
  };

  const updateGift = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/gifts/${editingGift.id}`, newGift);
      toast.success('Gift updated!');
      setShowEditDialog(false);
      setEditingGift(null);
      setNewGift({
        gift_name: '',
        recipient_name: '',
        recipient_id: '',
        price: 0,
        description: '',
        store_name: '',
        store_url: '',
        priority: 'must_have',
        notes: '',
        is_purchased: false
      });
      fetchGifts();
      fetchList();
    } catch (error) {
      toast.error('Failed to update gift');
    }
  };

  // Apply filters and sorting
  let filteredGifts = gifts.filter((gift) => {
    // Search filter
    const matchesSearch = gift.gift_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      gift.recipient_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Recipient filter
    const matchesRecipient = filterRecipient === 'all' || gift.recipient_name === filterRecipient;
    
    // Priority filter
    const matchesPriority = filterPriority === 'all' || gift.priority === filterPriority;
    
    // Price range filter
    let matchesPriceRange = true;
    if (filterPriceRange === 'under-25') matchesPriceRange = gift.price < 25;
    else if (filterPriceRange === '25-50') matchesPriceRange = gift.price >= 25 && gift.price < 50;
    else if (filterPriceRange === '50-100') matchesPriceRange = gift.price >= 50 && gift.price < 100;
    else if (filterPriceRange === '100-plus') matchesPriceRange = gift.price >= 100;
    
    return matchesSearch && matchesRecipient && matchesPriority && matchesPriceRange;
  });

  // Apply sorting
  filteredGifts = [...filteredGifts].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'name-asc':
        return a.gift_name.localeCompare(b.gift_name);
      case 'name-desc':
        return b.gift_name.localeCompare(a.gift_name);
      case 'recipient-asc':
        return a.recipient_name.localeCompare(b.recipient_name);
      case 'recipient-desc':
        return b.recipient_name.localeCompare(a.recipient_name);
      case 'date-asc':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'date-desc':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  // Categorize gifts for Elite users
  const giftsToBuy = filteredGifts.filter(g => !g.is_purchased && !g.is_received);
  const giftsOnTheWay = filteredGifts.filter(g => g.is_purchased && !g.is_received);
  const readyToWrap = filteredGifts.filter(g => g.is_received);

  const isElite = user?.subscription_tier === 'elite';
  
  // Get unique recipients for filter dropdown
  const uniqueRecipients = [...new Set(gifts.map(g => g.recipient_name))].sort();
  
  // Check if any filters are active
  const hasActiveFilters = filterRecipient !== 'all' || filterPriority !== 'all' || filterPriceRange !== 'all' || sortBy !== 'date-desc';
  
  const clearFilters = () => {
    setFilterRecipient('all');
    setFilterPriority('all');
    setFilterPriceRange('all');
    setSortBy('date-desc');
  };

  // Donut chart data for Elite users
  const chartData = isElite ? [
    { label: 'Gifts To Buy', count: giftsToBuy.length, color: '#dc2626' },
    { label: 'Gifts On The Way', count: giftsOnTheWay.length, color: '#f59e0b' },
    { label: 'Ready To Wrap', count: readyToWrap.length, color: '#16a34a' }
  ] : [];

  const totalGifts = gifts.length;
  const getDonutSegments = () => {
    if (totalGifts === 0) return [];
    
    let cumulativePercent = 0;
    return chartData.map(item => {
      const percent = (item.count / totalGifts) * 100;
      const segment = {
        ...item,
        percent,
        offset: cumulativePercent
      };
      cumulativePercent += percent;
      return segment;
    });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!list) return <div>List not found</div>;

  const budgetPercent = list.budget > 0 ? (list.total_spent / list.budget) * 100 : 0;

  // Helper function to render a gift card
  const renderGiftCard = (gift) => (
    <Card
      key={gift.id}
      className={`glass-card ${gift.is_purchased || gift.is_received ? 'opacity-75' : ''}`}
      data-testid={`gift-item-${gift.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex flex-col gap-2">
            <Checkbox
              checked={gift.is_purchased}
              onCheckedChange={() => togglePurchased(gift.id)}
              data-testid={`gift-checkbox-${gift.id}`}
            />
            {isElite && (
              <Checkbox
                checked={gift.is_received}
                onCheckedChange={() => toggleReceived(gift.id)}
                className="border-green-600 data-[state=checked]:bg-green-600"
                data-testid={`gift-received-checkbox-${gift.id}`}
                title="Mark as Received"
              />
            )}
          </div>
          {gift.image_url && (
            <img 
              src={gift.image_url} 
              alt={gift.gift_name}
              className="w-20 h-20 object-cover rounded border"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="flex-1">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className={`font-semibold ${gift.is_purchased || gift.is_received ? 'line-through text-gray-500' : ''}`}>
                  {gift.gift_name}
                </h4>
                <p className="text-sm text-gray-600">For: {gift.recipient_name}</p>
                {isElite && gift.is_received && (
                  <p className="text-xs text-green-600 font-medium mt-1">✓ Received</p>
                )}
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-lg font-bold text-red-600">{formatCurrency(gift.price)}</span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="p-2 hover:bg-blue-100 rounded transition-colors cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openEditDialog(gift);
                      return false;
                    }}
                    data-testid={`edit-gift-${gift.id}`}
                    title="Edit gift"
                  >
                    <Pencil className="w-4 h-4 text-blue-600 pointer-events-none" />
                  </button>
                  <button
                    type="button"
                    className="p-2 hover:bg-red-100 rounded transition-colors cursor-pointer"
                    onClick={(e) => handleDeleteClick(gift, e)}
                    data-testid={`delete-gift-${gift.id}`}
                    title="Delete gift"
                  >
                    <Trash2 className="w-4 h-4 text-red-600 pointer-events-none" />
                  </button>
                </div>
              </div>
            </div>
            {gift.store_name && (
              <p className="text-sm text-gray-600 mb-1">🏪 {gift.store_name}</p>
            )}
            {gift.notes && (
              <p className="text-sm text-gray-600 italic">{gift.notes}</p>
            )}
            {gift.recipient_id && recipients.find(r => r.id === gift.recipient_id) && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                {(() => {
                  const recipient = recipients.find(r => r.id === gift.recipient_id);
                  return (
                    <>
                      {recipient.interests.length > 0 && (
                        <p>💡 Loves: {recipient.interests.join(', ')}</p>
                      )}
                      {recipient.clothing_size && (
                        <p>👕 Size: {recipient.clothing_size}</p>
                      )}
                      {recipient.allergies.length > 0 && (
                        <p className="text-red-600">⚠️ Allergies: {recipient.allergies.join(', ')}</p>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            {gift.priority !== 'must_have' && (
              <span className="inline-block mt-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                {gift.priority === 'nice_to_have' ? 'Nice to Have' : 'Optional'}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-green-50" data-testid="list-detail-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-red-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* List Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            {list.event_type === 'christmas' ? '🎄' : '🎂'} {list.name}
          </h1>
          <p className="text-gray-600">{new Date(list.event_date).toLocaleDateString()}</p>
        </div>

        {/* Budget Tracker with Alerts */}
        <Card className="glass-card mb-8" data-testid="budget-tracker">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold">Budget Tracker</h3>
                {budgetPercent >= 100 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                    ⚠️ Over Budget
                  </span>
                )}
                {budgetPercent >= 80 && budgetPercent < 100 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                    ⚡ Approaching Limit
                  </span>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(list.total_spent)} <span className="text-gray-400">/</span> {formatCurrency(list.budget)}
                </p>
                <p className={`text-sm font-semibold ${budgetPercent >= 100 ? 'text-red-600' : budgetPercent >= 80 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {budgetPercent >= 100 ? 'Over by: ' : 'Remaining: '}
                  {formatCurrency(Math.abs(list.budget - list.total_spent))}
                </p>
              </div>
            </div>
            
            {/* Color-coded Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  budgetPercent >= 100 
                    ? 'bg-red-600' 
                    : budgetPercent >= 80 
                    ? 'bg-yellow-500' 
                    : budgetPercent >= 50
                    ? 'bg-green-500'
                    : 'bg-green-600'
                }`}
                style={{ width: `${Math.min(100, budgetPercent)}%` }}
              />
            </div>
            
            <div className="flex justify-between items-center mt-2">
              <p className="text-sm text-gray-600">
                {budgetPercent.toFixed(0)}% of budget used • {list.purchased_count} of {list.items_count} gifts purchased
              </p>
              {budgetPercent >= 100 && (
                <p className="text-xs text-red-600 font-medium">
                  {((budgetPercent - 100)).toFixed(0)}% over budget
                </p>
              )}
            </div>

            {/* Budget Alert Messages */}
            {budgetPercent >= 100 && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>⚠️ Budget Exceeded!</strong> You&apos;ve spent {formatCurrency(list.total_spent - list.budget)} more than your budget of {formatCurrency(list.budget)}.
                </p>
              </div>
            )}
            {budgetPercent >= 80 && budgetPercent < 100 && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>⚡ Approaching Budget Limit!</strong> You have {formatCurrency(list.budget - list.total_spent)} remaining ({(100 - budgetPercent).toFixed(0)}% of budget).
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gift Status Tracker (Elite Feature) */}
        {isElite && totalGifts > 0 && (
          <Card className="glass-card mb-8" data-testid="gift-status-tracker">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-xl font-semibold">Gift Status Tracker</h3>
                <Crown className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex items-center gap-8">
                {/* Donut Chart */}
                <div className="relative" style={{ width: '180px', height: '180px' }}>
                  <svg width="180" height="180" viewBox="0 0 180 180">
                    <circle
                      cx="90"
                      cy="90"
                      r="70"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="28"
                    />
                    {getDonutSegments().map((segment, index) => {
                      const circumference = 2 * Math.PI * 70;
                      const strokeDasharray = `${(segment.percent / 100) * circumference} ${circumference}`;
                      const rotation = (segment.offset / 100) * 360 - 90;
                      
                      return segment.count > 0 ? (
                        <circle
                          key={index}
                          cx="90"
                          cy="90"
                          r="70"
                          fill="none"
                          stroke={segment.color}
                          strokeWidth="28"
                          strokeDasharray={strokeDasharray}
                          strokeDashoffset="0"
                          transform={`rotate(${rotation} 90 90)`}
                          style={{ transition: 'all 0.3s ease' }}
                        />
                      ) : null;
                    })}
                    <text
                      x="90"
                      y="90"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="text-2xl font-bold"
                      fill="#1f2937"
                    >
                      {totalGifts}
                    </text>
                    <text
                      x="90"
                      y="108"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="text-sm"
                      fill="#6b7280"
                    >
                      Total Gifts
                    </text>
                  </svg>
                </div>

                {/* Legend */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {chartData.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.label}</p>
                        <p className="text-2xl font-bold" style={{ color: item.color }}>
                          {item.count}
                        </p>
                        <p className="text-xs text-gray-600">
                          {totalGifts > 0 ? Math.round((item.count / totalGifts) * 100) : 0}%
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions Bar */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700" data-testid="add-gift-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Gift
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="add-gift-dialog">
              <DialogHeader>
                <DialogTitle>🎁 Add a Gift</DialogTitle>
              </DialogHeader>
              <form onSubmit={addGift} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Gift Name *</label>
                    <Input
                      placeholder="Sweater for Mom"
                      value={newGift.gift_name}
                      onChange={(e) => setNewGift({ ...newGift, gift_name: e.target.value })}
                      required
                      data-testid="gift-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Recipient *</label>
                    {recipients.length > 0 ? (
                      <Select 
                        value={newGift.recipient_id || 'custom'} 
                        onValueChange={(val) => {
                          if (val === 'custom') {
                            setNewGift({ ...newGift, recipient_id: '', recipient_name: '' });
                          } else {
                            const recipient = recipients.find(r => r.id === val);
                            setNewGift({ ...newGift, recipient_id: val, recipient_name: recipient?.name || '' });
                          }
                        }}
                      >
                        <SelectTrigger data-testid="recipient-select">
                          <SelectValue placeholder="Select recipient" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">Enter custom name</SelectItem>
                          {recipients.map((recipient) => (
                            <SelectItem key={recipient.id} value={recipient.id}>
                              {recipient.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder="Mom"
                        value={newGift.recipient_name}
                        onChange={(e) => setNewGift({ ...newGift, recipient_name: e.target.value })}
                        required
                        data-testid="recipient-name-input"
                      />
                    )}
                    {newGift.recipient_id === '' && recipients.length > 0 && (
                      <Input
                        placeholder="Enter recipient name"
                        value={newGift.recipient_name}
                        onChange={(e) => setNewGift({ ...newGift, recipient_name: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="custom-recipient-input"
                      />
                    )}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Price *</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="45.00"
                      value={newGift.price || ''}
                      onChange={(e) => setNewGift({ ...newGift, price: parseFloat(e.target.value) || 0 })}
                      required
                      data-testid="gift-price-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Priority</label>
                    <Select value={newGift.priority} onValueChange={(val) => setNewGift({ ...newGift, priority: val })}>
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="must_have">Must Have</SelectItem>
                        <SelectItem value="nice_to_have">Nice to Have</SelectItem>
                        <SelectItem value="optional">Optional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Store</label>
                    <Input
                      placeholder="Target"
                      value={newGift.store_name}
                      onChange={(e) => setNewGift({ ...newGift, store_name: e.target.value })}
                      data-testid="store-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Product URL</label>
                    <Input
                      type="url"
                      placeholder="https://..."
                      value={newGift.store_url}
                      onChange={(e) => setNewGift({ ...newGift, store_url: e.target.value })}
                      data-testid="store-url-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Image URL (optional)</label>
                  <Input
                    type="url"
                    placeholder="https://image-url.jpg"
                    value={newGift.image_url}
                    onChange={(e) => setNewGift({ ...newGift, image_url: e.target.value })}
                    data-testid="image-url-input"
                  />
                  {newGift.image_url && (
                    <div className="mt-2">
                      <img 
                        src={newGift.image_url} 
                        alt="Gift preview" 
                        className="max-w-[200px] max-h-[200px] object-contain rounded border"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Notes</label>
                  <Textarea
                    placeholder="Size medium, blue color"
                    value={newGift.notes}
                    onChange={(e) => setNewGift({ ...newGift, notes: e.target.value })}
                    data-testid="gift-notes-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={newGift.is_purchased}
                    onCheckedChange={(checked) => setNewGift({ ...newGift, is_purchased: checked })}
                    data-testid="mark-purchased-checkbox"
                  />
                  <label className="text-sm">Mark as already purchased</label>
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="submit-gift-button">
                  🎄 Add Gift
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Gift Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-2xl" data-testid="edit-gift-dialog">
              <DialogHeader>
                <DialogTitle>✏️ Edit Gift</DialogTitle>
              </DialogHeader>
              <form onSubmit={updateGift} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Gift Name *</label>
                    <Input
                      placeholder="Sweater for Mom"
                      value={newGift.gift_name}
                      onChange={(e) => setNewGift({ ...newGift, gift_name: e.target.value })}
                      required
                      data-testid="edit-gift-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Recipient *</label>
                    {recipients.length > 0 ? (
                      <Select 
                        value={newGift.recipient_id || 'custom'} 
                        onValueChange={(val) => {
                          if (val === 'custom') {
                            setNewGift({ ...newGift, recipient_id: '', recipient_name: '' });
                          } else {
                            const recipient = recipients.find(r => r.id === val);
                            setNewGift({ ...newGift, recipient_id: val, recipient_name: recipient?.name || '' });
                          }
                        }}
                      >
                        <SelectTrigger data-testid="edit-recipient-select">
                          <SelectValue placeholder="Select recipient" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">Enter custom name</SelectItem>
                          {recipients.map((recipient) => (
                            <SelectItem key={recipient.id} value={recipient.id}>
                              {recipient.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder="Mom"
                        value={newGift.recipient_name}
                        onChange={(e) => setNewGift({ ...newGift, recipient_name: e.target.value })}
                        required
                        data-testid="edit-recipient-name-input"
                      />
                    )}
                    {newGift.recipient_id === '' && recipients.length > 0 && (
                      <Input
                        placeholder="Enter recipient name"
                        value={newGift.recipient_name}
                        onChange={(e) => setNewGift({ ...newGift, recipient_name: e.target.value })}
                        required
                        className="mt-2"
                        data-testid="edit-custom-recipient-input"
                      />
                    )}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Price *</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="45.00"
                      value={newGift.price || ''}
                      onChange={(e) => setNewGift({ ...newGift, price: parseFloat(e.target.value) || 0 })}
                      required
                      data-testid="edit-gift-price-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Priority</label>
                    <Select value={newGift.priority} onValueChange={(val) => setNewGift({ ...newGift, priority: val })}>
                      <SelectTrigger data-testid="edit-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="must_have">Must Have</SelectItem>
                        <SelectItem value="nice_to_have">Nice to Have</SelectItem>
                        <SelectItem value="optional">Optional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Store</label>
                  <Input
                    placeholder="Target"
                    value={newGift.store_name}
                    onChange={(e) => setNewGift({ ...newGift, store_name: e.target.value })}
                    data-testid="edit-store-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Notes</label>
                  <Textarea
                    placeholder="Size medium, blue color"
                    value={newGift.notes}
                    onChange={(e) => setNewGift({ ...newGift, notes: e.target.value })}
                    data-testid="edit-gift-notes-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={newGift.is_purchased}
                    onCheckedChange={(checked) => setNewGift({ ...newGift, is_purchased: checked })}
                    data-testid="edit-mark-purchased-checkbox"
                  />
                  <label className="text-sm">Mark as purchased</label>
                </div>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowEditDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="update-gift-button">
                    ✏️ Update Gift
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Import from URL Button */}
          <Dialog open={showUrlImportDialog} onOpenChange={setShowUrlImportDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" data-testid="import-url-button">
                <Download className="w-4 h-4 mr-2" />
                Import from URL
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>🔗 Import Gift from URL</DialogTitle>
              </DialogHeader>
              <form onSubmit={importFromUrl} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Product URL
                  </label>
                  <Input
                    type="url"
                    placeholder="https://www.amazon.com/product..."
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    required
                    data-testid="url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste a link from Amazon, Target, or any online store
                  </p>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={extracting}
                  data-testid="extract-button"
                >
                  {extracting ? 'Extracting...' : 'Extract Gift Details'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Export Buttons (Premium) */}
          {user?.subscription_tier !== 'free' && (
            <>
              <Button
                variant="outline"
                onClick={exportCSV}
                disabled={exporting || gifts.length === 0}
                data-testid="export-csv-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                onClick={exportPDF}
                disabled={exporting || gifts.length === 0}
                data-testid="export-pdf-button"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </>
          )}

          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search gifts..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Filters & Sort Bar */}
        <div className="flex flex-wrap gap-3 mb-6 items-center bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filter:</span>
            
            <Select value={filterRecipient} onValueChange={setFilterRecipient}>
              <SelectTrigger className="w-[160px]" data-testid="filter-recipient">
                <SelectValue placeholder="Recipient" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Recipients</SelectItem>
                {uniqueRecipients.map((recipient) => (
                  <SelectItem key={recipient} value={recipient}>
                    {recipient}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-[140px]" data-testid="filter-priority">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="must_have">Must Have</SelectItem>
                <SelectItem value="nice_to_have">Nice to Have</SelectItem>
                <SelectItem value="optional">Optional</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterPriceRange} onValueChange={setFilterPriceRange}>
              <SelectTrigger className="w-[140px]" data-testid="filter-price">
                <SelectValue placeholder="Price" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prices</SelectItem>
                <SelectItem value="under-25">Under $25</SelectItem>
                <SelectItem value="25-50">$25 - $50</SelectItem>
                <SelectItem value="50-100">$50 - $100</SelectItem>
                <SelectItem value="100-plus">$100+</SelectItem>
              </SelectContent>
            </Select>

            <div className="h-6 w-px bg-gray-300 mx-2" />

            <span className="text-sm font-medium text-gray-700">Sort:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]" data-testid="sort-by">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date-desc">Newest First</SelectItem>
                <SelectItem value="date-asc">Oldest First</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
                <SelectItem value="name-asc">Name: A to Z</SelectItem>
                <SelectItem value="name-desc">Name: Z to A</SelectItem>
                <SelectItem value="recipient-asc">Recipient: A to Z</SelectItem>
                <SelectItem value="recipient-desc">Recipient: Z to A</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="clear-filters"
              >
                Clear Filters
              </Button>
            )}
          </div>

          <div className="ml-auto text-sm text-gray-600">
            {filteredGifts.length} {filteredGifts.length === 1 ? 'gift' : 'gifts'}
          </div>
        </div>

        {/* Gifts List */}
        {isElite && filteredGifts.length > 0 ? (
          <div className="space-y-8">
            {/* Gifts To Buy Section */}
            {giftsToBuy.length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-red-600"></div>
                  Gifts To Buy ({giftsToBuy.length})
                </h3>
                <div className="space-y-4">
                  {giftsToBuy.map((gift) => renderGiftCard(gift))}
                </div>
              </div>
            )}

            {/* Gifts On The Way Section */}
            {giftsOnTheWay.length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-amber-500"></div>
                  Gifts On The Way ({giftsOnTheWay.length})
                </h3>
                <div className="space-y-4">
                  {giftsOnTheWay.map((gift) => renderGiftCard(gift))}
                </div>
              </div>
            )}

            {/* Ready To Wrap Section */}
            {readyToWrap.length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-600"></div>
                  Ready To Wrap ({readyToWrap.length})
                </h3>
                <div className="space-y-4">
                  {readyToWrap.map((gift) => renderGiftCard(gift))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredGifts.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <p className="text-gray-600 mb-4">
                    {searchQuery ? 'No gifts match your search' : 'No gifts added yet'}
                  </p>
                  {!searchQuery && (
                    <Button
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => setShowAddDialog(true)}
                      data-testid="add-first-gift-button"
                    >
                      Add Your First Gift
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              filteredGifts.map((gift) => renderGiftCard(gift))
            )}
          </div>
        )}
      </main>

      {/* Delete Gift Confirmation Dialog */}
      <AlertDialog open={!!giftToDelete} onOpenChange={(open) => !open && setGiftToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Gift?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{giftToDelete?.gift_name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteGift}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ListDetail;
